import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { allowancePeriodKeys, ptoBalanceKeys } from "@/lib/query";

export interface AllowancePeriod {
  id: string;
  periodStart: string;
  periodEnd: string;
  totalAllowanceHours: number;
  createdAt: string;
  updatedAt: string;
}

export interface AllowancePeriodInput {
  periodStart?: string;
  periodEnd?: string;
  totalAllowanceHours?: number;
}

// Singleton — GET + PUT only, no create/delete hooks (see .claude/rules/api.md
// note in src/api/allowance-period.ts).
export function useAllowancePeriod() {
  return useQuery({
    queryKey: allowancePeriodKeys.detail(),
    queryFn: async (): Promise<AllowancePeriod> => {
      const res = await fetch("/api/allowance-period");
      if (!res.ok) throw new Error("Failed to fetch allowance period");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useUpdateAllowancePeriod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: AllowancePeriodInput): Promise<AllowancePeriod> => {
      const res = await fetch("/api/allowance-period", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => null);
        throw new Error(json?.error ?? "Update failed");
      }
      const json = await res.json();
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: allowancePeriodKeys.all });
      queryClient.invalidateQueries({ queryKey: ptoBalanceKeys.all });
      toast.success("Allowance updated");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to update allowance");
    },
  });
}
